import * as tslib_1 from "tslib";
import * as intersection from 'lodash/intersection';
import { Filter, FilterKeys } from './filters';
import { QueryValidationReason } from '../models/query-validation-result.model';
import { isArray, isNumeric } from '../../util/helpers';
/**
 * Minimum runway length filter. The filter passes only if the given airport search result has at least one runway
 * whose length is equal to or greater the value of this filter.
 */
var RunwayLengthFilter = /** @class */ (function (_super) {
    tslib_1.__extends(RunwayLengthFilter, _super);
    function RunwayLengthFilter(length) {
        if (length === void 0) { length = null; }
        var _this = _super.call(this, {
            key: FilterKeys.RunwayLength,
            apply: function (airport) { return _this._apply(airport); },
            encode: function () { return _this._encode(); },
            decode: function (v) { return _this._decode(v); },
            validate: function () { return _this._validate(); }
        }) || this;
        _this.length = length;
        return _this;
    }
    RunwayLengthFilter.prototype._apply = function (airport) {
        return airport.maxRunwayLength >= this.length;
    };
    RunwayLengthFilter.prototype._encode = function () {
        return this.enabled && this.length ? this.length.toString() : null;
    };
    RunwayLengthFilter.prototype._decode = function (v) {
        if (isNumeric(v)) {
            this.length = parseInt(v.toString(), 10);
            return true;
        }
        return false;
    };
    RunwayLengthFilter.prototype._validate = function () {
        if (this.length <= 0) {
            return {
                valid: false,
                reason: QueryValidationReason.MIN_RUNWAY_LENGTH,
                message: 'Minimum runway length must be greater than zero.'
            };
        }
        return { valid: true };
    };
    return RunwayLengthFilter;
}(Filter));
export { RunwayLengthFilter };
/**
 * Fuel type filter. The filter passes only if the given airport search result instance has the given fuel type
 * available.
 */
var FuelFilter = /** @class */ (function (_super) {
    tslib_1.__extends(FuelFilter, _super);
    function FuelFilter(fuelType, additionalTypes) {
        if (additionalTypes === void 0) { additionalTypes = []; }
        var _this = _super.call(this, {
            key: FilterKeys.FuelFilter,
            multi: true,
            apply: function (airport) { return _this._apply(airport); },
            encode: function () { return _this._encode(); },
            decode: function (v) { return _this._decode(v); },
            validate: function () { return _this._validate(); }
        }) || this;
        _this.fuelType = fuelType;
        _this.additionalTypes = additionalTypes;
        return _this;
    }
    FuelFilter.prototype._apply = function (airport) {
        var availableTypes = airport.fuel.map(function (f) { return f.fuelType; });
        var validTypes = [].concat(this.fuelType, this.additionalTypes);
        return intersection(availableTypes, validTypes).length > 0;
    };
    FuelFilter.prototype._encode = function () {
        return this.enabled ? this.fuelType : null;
    };
    FuelFilter.prototype._decode = function (v) {
        var _this = this;
        if (isArray(v)) {
            return v.some(function (f) { return _this.fuelType === f; });
        }
        return false;
    };
    FuelFilter.prototype._validate = function () {
        return { valid: true };
    };
    return FuelFilter;
}(Filter));
export { FuelFilter };
/**
 * Approach type filter. The filter passes only if the airport search result instance has at least one instrument
 * approach of the specified type.
 */
var ApproachFilter = /** @class */ (function (_super) {
    tslib_1.__extends(ApproachFilter, _super);
    function ApproachFilter(approach) {
        var _this = _super.call(this, {
            key: FilterKeys.Approach,
            multi: true,
            apply: function (airport) { return _this._apply(airport); },
            encode: function () { return _this._encode(); },
            decode: function (v) { return _this._decode(v); },
            validate: function () { return _this._validate(); }
        }) || this;
        _this.approach = approach;
        return _this;
    }
    ApproachFilter.prototype._apply = function (airport) {
        return this.enabled && airport.approaches.includes(this.approach);
    };
    ApproachFilter.prototype._encode = function () {
        return this.enabled ? this.approach : null;
    };
    ApproachFilter.prototype._decode = function (v) {
        var _this = this;
        if (isArray(v)) {
            return v.some(function (f) { return _this.approach === f; });
        }
        return false;
    };
    ApproachFilter.prototype._validate = function () {
        return { valid: true };
    };
    return ApproachFilter;
}(Filter));
export { ApproachFilter };
/**
 * Airport service type
 */
export var ServiceType;
(function (ServiceType) {
    ServiceType["AirframeRepair"] = "airframe";
    ServiceType["EngineRepair"] = "engine";
    ServiceType["AvionicsRepair"] = "avionics";
})(ServiceType || (ServiceType = {}));
/**
 * Service filter. The filter passes only if the airport search result instance offers the corresponding service
 * (e.g., airframe or engine repair).
 */
var ServiceFilter = /** @class */ (function (_super) {
    tslib_1.__extends(ServiceFilter, _super);
    function ServiceFilter(service) {
        var _this = _super.call(this, {
            key: FilterKeys.Services,
            multi: true,
            apply: function (airport) { return _this._apply(airport); },
            encode: function () { return _this._encode(); },
            decode: function (v) { return _this._decode(v); },
            validate: function () { return _this._validate(); }
        }) || this;
        _this.service = service;
        return _this;
    }
    ServiceFilter.prototype._apply = function (airport) {
        if (!this.enabled) {
            return false;
        }
        switch (this.service) {
            case ServiceType.AirframeRepair:
                return airport.airframeRepair;
            case ServiceType.EngineRepair:
                return airport.engineRepair;
            case ServiceType.AvionicsRepair:
                return airport.avionicsRepair;
        }
        return false;
    };
    ServiceFilter.prototype._encode = function () {
        return this.enabled ? this.service : null;
    };
    ServiceFilter.prototype._decode = function (v) {
        var _this = this;
        if (isArray(v)) {
            return v.some(function (f) { return _this.service === f; });
        }
        return false;
    };
    ServiceFilter.prototype._validate = function () {
        return { valid: true };
    };
    return ServiceFilter;
}(Filter));
export { ServiceFilter };
